/**
 * II-Agent Module
 * Handles all interactions with the II-Agent canister for infrastructure analysis
 *
 * @module IIAgent
 * Sprint 32: OOA/OOD Refactoring - Facade pattern using extracted services
 */

// Import types and services from extracted modules
import {
    InfrastructureReport,
    Anomaly,
    SecurityFinding,
    CostRecommendation,
    ServiceMap,
    ServiceMapTier,
    ServiceMapDevice,
    ServiceMapConnection,
    GraphNode,
    GraphEdge,
    GraphUpdate,
    ServiceMapGenerationStep,
    ServiceMapGenerationResponse,
    QueryResponse
} from './types/ii-agent-types';

import { AnalysisHistory } from './utils/analysis-history';
import { CandidParser } from './parsers/candid-parser';
import { ResponseFormatter } from './formatters/response-formatter';
import { AnalysisService } from './services/analysis-service';
import { ServiceMapService } from './services/service-map-service';
import { NLPQueryRouter } from './services/nlp-query-router';

/**
 * CanisterClient interface for type safety
 */
interface CanisterClient {
    callCanister(canister: string, method: string, args: any[]): Promise<any>;
}

/**
 * Result type for canister responses
 */

/**
 * II-Agent class for infrastructure intelligence
 * Refactored to use OOA/OOD principles - acts as a facade
 */
export class IIAgent {
    private client: CanisterClient | null = null;
    private analysisHistory: AnalysisHistory;

    // Extracted services
    private analysisService: AnalysisService | null = null;
    private serviceMapService: ServiceMapService | null = null;
    private nlpQueryRouter: NLPQueryRouter | null = null;
    private candidParser: CandidParser;
    private responseFormatter: ResponseFormatter;

    constructor() {
        this.analysisHistory = new AnalysisHistory();
        this.candidParser = new CandidParser();
        this.responseFormatter = new ResponseFormatter();
    }

    /**
     * Initialize the II-Agent module
     */
    async initialize(canisterClient: CanisterClient): Promise<void> {
        this.client = canisterClient;

        // Initialize services with the canister client
        this.analysisService = new AnalysisService(canisterClient, this.analysisHistory);
        this.serviceMapService = new ServiceMapService(canisterClient, this.candidParser, this.analysisHistory);
        this.nlpQueryRouter = new NLPQueryRouter(
            this.analysisService,
            this.responseFormatter,
            this.analysisHistory
        );

        console.log('II-Agent module initialized with OOA/OOD services');
    }

    /**
     * Set the vector_db principal for II-Agent
     */
    async configureVectorDB(vectorDbPrincipal: string): Promise<any> {
        if (!this.client) {
            throw new Error('II-Agent not initialized');
        }
        const result = await this.client.callCanister('ii_agent', 'set_vector_db_principal', [vectorDbPrincipal]);
        console.log('Vector DB principal configured:', result);
        return result;
    }

    // ========================================================================
    // Analysis Methods - Delegated to AnalysisService
    // ========================================================================

    /**
     * Get infrastructure analysis
     */
    async analyzeInfrastructure(): Promise<InfrastructureReport> {
        if (!this.analysisService) {
            throw new Error('II-Agent not initialized');
        }
        return this.analysisService.analyzeInfrastructure();
    }

    /**
     * Get infrastructure analysis filtered by location
     */
    async analyzeInfrastructureByLocation(location: string): Promise<InfrastructureReport> {
        if (!this.analysisService) {
            throw new Error('II-Agent not initialized');
        }
        return this.analysisService.analyzeInfrastructureByLocation(location);
    }

    /**
     * Detect anomalies in infrastructure
     */
    async detectAnomalies(threshold: number = 0.5): Promise<Anomaly[]> {
        if (!this.analysisService) {
            throw new Error('II-Agent not initialized');
        }
        return this.analysisService.detectAnomalies(threshold);
    }

    /**
     * Audit security compliance
     */
    async auditSecurity(): Promise<SecurityFinding[]> {
        if (!this.analysisService) {
            throw new Error('II-Agent not initialized');
        }
        return this.analysisService.auditSecurity();
    }

    /**
     * Get cost optimization recommendations
     */
    async recommendCostOptimizations(): Promise<CostRecommendation[]> {
        if (!this.analysisService) {
            throw new Error('II-Agent not initialized');
        }
        return this.analysisService.recommendCostOptimizations();
    }

    /**
     * Get all findings
     */
    async getAllFindings(): Promise<any[]> {
        if (!this.analysisService) {
            throw new Error('II-Agent not initialized');
        }
        return this.analysisService.getAllFindings();
    }

    /**
     * Get findings by severity
     */
    async getFindingsBySeverity(severity: string): Promise<any[]> {
        if (!this.analysisService) {
            throw new Error('II-Agent not initialized');
        }
        return this.analysisService.getFindingsBySeverity(severity);
    }

    /**
     * Get all recommendations
     */
    async getAllRecommendations(): Promise<any[]> {
        if (!this.analysisService) {
            throw new Error('II-Agent not initialized');
        }
        return this.analysisService.getAllRecommendations();
    }

    /**
     * Get II-Agent statistics
     */
    async getStats(): Promise<any[]> {
        if (!this.client) {
            throw new Error('II-Agent not initialized');
        }
        console.log('📊 Calling ii_agent.get_stats...');
        const result = await this.client.callCanister('ii_agent', 'get_stats', []);
        console.log('📊 Raw result from canister:', result);
        return result || [];
    }

    // ========================================================================
    // NLP Query Methods - Delegated to NLPQueryRouter
    // ========================================================================

    /**
     * Process natural language query
     */
    async processNaturalLanguageQuery(query: string): Promise<QueryResponse> {
        if (!this.nlpQueryRouter) {
            throw new Error('II-Agent not initialized');
        }
        return this.nlpQueryRouter.processQuery(query);
    }

    /**
     * Extract location from natural language query
     */
    extractLocation(query: string): string | null {
        if (!this.nlpQueryRouter) {
            return null;
        }
        return this.nlpQueryRouter.extractLocation(query);
    }

    // ========================================================================
    // Response Formatting - Delegated to ResponseFormatter
    // ========================================================================

    /**
     * Format infrastructure report as natural language
     */
    formatInfrastructureResponse(report: InfrastructureReport, location: string | null = null): string {
        return this.responseFormatter.formatInfrastructure(report, location);
    }

    /**
     * Format anomalies as natural language
     */
    formatAnomaliesResponse(anomalies: Anomaly[]): string {
        return this.responseFormatter.formatAnomalies(anomalies);
    }

    /**
     * Format security findings as natural language
     */
    formatSecurityResponse(findings: SecurityFinding[]): string {
        return this.responseFormatter.formatSecurity(findings);
    }

    /**
     * Format cost recommendations as natural language
     */
    formatCostResponse(recommendations: CostRecommendation[]): string {
        return this.responseFormatter.formatCost(recommendations);
    }

    // ========================================================================
    // Service Map Methods - Delegated to ServiceMapService
    // ========================================================================

    /**
     * Parse raw Candid string to JavaScript object for ServiceMap
     */
    parseCandidServiceMap(candidString: string | ServiceMap): ServiceMap {
        return this.candidParser.parseServiceMap(candidString);
    }

    /**
     * Generate service map from natural language prompt
     */
    async generateServiceMap(prompt: string): Promise<ServiceMap> {
        if (!this.serviceMapService) {
            throw new Error('II-Agent not initialized');
        }
        return this.serviceMapService.generate(prompt);
    }

    /**
     * Generate service map with live step-by-step updates
     */
    async generateServiceMapLive(prompt: string): Promise<ServiceMapGenerationResponse> {
        if (!this.serviceMapService) {
            throw new Error('II-Agent not initialized');
        }
        return this.serviceMapService.generateLive(prompt);
    }
}

// Global instance
declare global {
    interface Window {
        iiAgent: IIAgent;
    }
}

window.iiAgent = new IIAgent();

// Re-export types for backwards compatibility
export type {
    InfrastructureReport,
    Anomaly,
    SecurityFinding,
    CostRecommendation,
    ServiceMap,
    ServiceMapTier,
    ServiceMapDevice,
    ServiceMapConnection,
    GraphNode,
    GraphEdge,
    GraphUpdate,
    ServiceMapGenerationStep,
    ServiceMapGenerationResponse
};
